/**
 * CSRF Token Management
 *
 * Implements CSRF protection for cookie-based authentication endpoints.
 * The token is stored in memory and fetched LAZILY from the backend.
 *
 * Security Notes:
 * - Token is stored in memory (not localStorage/sessionStorage)
 * - Token is fetched lazily on first use (not on app load)
 * - Only needed for /auth/refresh and /auth/logout endpoints
 * - Uses double-submit cookie pattern (token + httpOnly cookie)
 */

import { getApiUrl } from '@/utils/env'
import { logger } from '@/lib/logger'

// In-memory storage (never persisted)
let csrfToken: string | null = null
let isFetching = false
let fetchPromise: Promise<string> | null = null

// Timeout for CSRF token fetch (10 seconds)
const CSRF_FETCH_TIMEOUT_MS = 10000

/**
 * Initialize CSRF token by fetching from backend
 * This is now called LAZILY when first needed
 */
export async function initCsrf(): Promise<string> {
  // If we already have a token, return it
  if (csrfToken) {
    return csrfToken
  }

  // If already fetching, wait for that request to complete
  if (isFetching && fetchPromise) {
    return fetchPromise
  }

  const apiUrl = getApiUrl()

  isFetching = true
  fetchPromise = (async () => {
    // Create abort controller with timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      controller.abort(new Error('CSRF token fetch timed out'))
    }, CSRF_FETCH_TIMEOUT_MS)

    try {
      const response = await fetch(`${apiUrl}/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include', // Important: sends cookies
        headers: {
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`Failed to fetch CSRF token: ${response.status}`)
      }

      const data = await response.json()
      csrfToken = data.data?.csrfToken || data.csrfToken || null

      if (!csrfToken) {
        throw new Error('CSRF token not found in response')
      }

      return csrfToken
    } catch (error) {
      clearTimeout(timeoutId)

      // Handle timeout specifically
      if (error instanceof Error && error.name === 'AbortError') {
        logger.warn('⚠️ [CSRF] Token fetch timed out')
        throw new Error('CSRF token fetch timed out')
      }

      logger.error('❌ [CSRF] Failed to fetch token:', error)
      throw error
    } finally {
      isFetching = false
      fetchPromise = null
    }
  })()

  return fetchPromise
}

/**
 * Get the current CSRF token from memory
 * If not initialized yet, this will trigger a fetch
 * Returns null if fetch fails (non-blocking)
 */
export async function getCsrfTokenAsync(): Promise<string | null> {
  try {
    if (!csrfToken && !isFetching) {
      await initCsrf()
    }
    return csrfToken
  } catch (error) {
    logger.warn('⚠️ [CSRF] Could not get token:', error)
    return null
  }
}

/**
 * Get the current CSRF token from memory (synchronous)
 * Returns null if not initialized yet
 */
export function getCsrfToken(): string | null {
  return csrfToken
}

/**
 * Clear the CSRF token from memory
 * Useful when logging out or handling security events
 */
export function clearCsrfToken(): void {
  csrfToken = null
  isFetching = false
  fetchPromise = null
}

/**
 * Check if CSRF token is initialized
 */
export function hasCsrfToken(): boolean {
  return csrfToken !== null
}

/**
 * Refresh CSRF token (re-fetch from backend)
 * Useful when handling 403 errors
 */
export async function refreshCsrfToken(): Promise<string> {
  logger.log('🔄 [CSRF] Refreshing token...')
  clearCsrfToken() // Clear existing token first
  return initCsrf()
}
